import React, { useState } from 'react';
import { Columns3 } from 'lucide-react';
import { COLUMN_BY_KEY, GROUP_ORDER } from './columnRegistry';
import Popover from './Popover';
import Button from '../ui/Button';
import { Checkbox } from '../pilot-ui/checkbox';
import { CLIP_LIFECYCLE_DOC_URL, CLIP_LIFECYCLE_DOC_TITLE } from '../../utils/docLinks';

/**
 * Plan 101 — column chooser popover: grouped visibility checkboxes over the
 * registry plus "Reset to defaults" (visibility, order, AND widths).
 * Visibility is pure client state — the API always returns the full wide row.
 */
export const ColumnChooser: React.FC<{
  /** Full merged column order (every registry key). */
  columnOrder: string[];
  hidden: Set<string>;
  onToggle: (key: string, visible: boolean) => void;
  onReset: () => void;
}> = ({ columnOrder, hidden, onToggle, onReset }) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover
      open={open}
      onClose={() => setOpen(false)}
      align="right"
      label="Choose columns"
      panelClassName="w-72 max-h-96 overflow-y-auto p-3"
      trigger={
        <Button
          variant="secondary"
          onClick={() => setOpen((o) => !o)}
          className={open ? 'bg-accent text-accent-foreground hover:bg-accent/90' : undefined}
          aria-label="Choose columns"
          aria-expanded={open}
          title="Choose columns"
        >
          <Columns3 size={18} aria-hidden />
        </Button>
      }
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold">Columns</span>
        <Button
          variant="link"
          size="sm"
          onClick={onReset}
          title="Put the columns, their order and widths back to the standard layout"
        >
          Reset to defaults
        </Button>
      </div>
      {GROUP_ORDER.map((group) => {
        const keys = columnOrder.filter((k) => COLUMN_BY_KEY[k]?.group === group);
        if (keys.length === 0) return null;
        return (
          <div key={group} className="mb-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
              {group}
              {group === 'Lifecycle' && (
                <a
                  href={CLIP_LIFECYCLE_DOC_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={CLIP_LIFECYCLE_DOC_TITLE}
                  className="ml-2 normal-case tracking-normal font-normal text-link hover:underline"
                >
                  what these mean
                </a>
              )}
            </h4>
            <ul>
              {keys.map((key) => {
                const col = COLUMN_BY_KEY[key];
                return (
                  <li key={key}>
                    <label className="flex items-center gap-2 text-sm px-1 py-0.5 rounded-sm hover:bg-gray-50 cursor-pointer">
                      <Checkbox
                        className="size-6"
                        checked={!hidden.has(key)}
                        onCheckedChange={(checked) => onToggle(key, checked === true)}
                        aria-label={`Show ${col.label} column`}
                        title={`Show or hide the ${col.label} column`}
                      />
                      <span className="truncate">{col.label}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </Popover>
  );
};

export default ColumnChooser;
